<script setup lang="ts">
import { watch } from 'vue';
import {
  deleteThemeQueryGenerationRequest,
  getThemeQueryGenerationRequest,
} from '@/api/themeQueryGenerationRequest';
import AutoResizeTextarea from '@/components/common/atoms/auto-resize-textarea.vue';
import HistoryDropdownInput from '@/components/common/history-dropdown-input/history-dropdown-input.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { useThemeSettingSupportModal } from '@/components/theme/edit/theme-setting-support-modal/useThemeSettingSupportModal';
import { STATES, useThemeQueryGenerationRequests } from '@/utils/swr';

const inputText = defineModel<string>({ required: true });

const { placeholder = '生成AIの法規制について調べたい', shouldTrack = true } =
  defineProps<{
    placeholder?: string;
    shouldTrack?: boolean;
  }>();

const emit = defineEmits<{
  selectHistory: [id: number];
  submit: [];
}>();

const { createSnackbar } = useSnackbar();

const { trackAIKeywordsRequest } = useThemeSettingSupportModal();

const {
  data: histories,
  mutate: mutateThemeQueryGenerationRequests,
  state: stateThemeQueryGenerationRequests,
} = useThemeQueryGenerationRequests('updated_at', 'desc');

watch(stateThemeQueryGenerationRequests, () => {
  if (stateThemeQueryGenerationRequests.value === STATES.ERROR) {
    createSnackbar({
      message: 'クエリ生成履歴の取得に失敗しました',
      type: 'error',
    });
  }
});

const deleteHistoryHandler = async (index: number) => {
  const deletedItemId = histories.value?.[index]?.id;
  if (!deletedItemId) return;
  const result = await deleteThemeQueryGenerationRequest(deletedItemId);
  if (result.isSuccess) {
    mutateThemeQueryGenerationRequests();
  } else {
    createSnackbar({
      message: result.message,
      description: result.description,
      type: 'error',
    });
  }
};

const selectHistory = async (index: number) => {
  const selectedItemId = histories.value?.[index]?.id;
  if (!selectedItemId) return;
  const result = await getThemeQueryGenerationRequest(selectedItemId);
  if (result.isSuccess && shouldTrack) {
    await trackAIKeywordsRequest(result.data);
  }
  emit('selectHistory', selectedItemId);
};

const submit = () => {
  inputText.value = inputText.value
    .split('\n')
    .map(line => line.trim())
    .join('\n');
  emit('submit');
};
</script>
<template>
  <HistoryDropdownInput
    v-if="histories"
    @delete-history="deleteHistoryHandler"
    @select-from-history="selectHistory"
    @select-from-input="submit"
    v-model:input-value="inputText"
    :histories="histories"
    display-key-name="request_text"
    :max-visible-items="5"
    :enable-shift-enter-newline="true"
  >
    <template #default="{ handlers }">
      <AutoResizeTextarea
        class="auto-resize-textarea"
        :handlers="handlers"
        v-model="inputText"
      >
        <template #default="{ handleInput, filteredHandlers }">
          <textarea
            class="c-text-regular--m textarea"
            :value="inputText"
            @input="handleInput"
            maxlength="200"
            v-on="filteredHandlers"
            :placeholder
            rows="1"
          ></textarea>
        </template>
      </AutoResizeTextarea>
    </template>
  </HistoryDropdownInput>
</template>

<style lang="scss" scoped>
.textarea {
  border: none;
  min-height: 1.5em;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  box-sizing: border-box;
}

textarea::placeholder {
  color: $color-gray600;
}
</style>
